import { computed, Ref } from 'vue';
import { getLabel, getTranslation } from '@/utils/data-display-utils';
import { constant } from '@/constant';
import { ParticipationState } from '@/store/consultation.interface';
import { Locales } from '@/store/language.interface';

export interface GuidedConsultationForm {
  id: number;
  title: string;
  sectionOrderNum: number;
}

export interface GuidedConsultationSectionProgress {
  sectionOrderNum: number;
  visibleCount: number;
  answeredCount: number;
  pct: number;
}

export function useGuidedConsultationProgress(
  participationState: Ref<ParticipationState>,
  languageEnabled: Ref<Locales[]>,
) {
  const summary = computed(() => participationState.value.summary);

  const isCompleted = computed(
    () => participationState.value.status === constant.ConsultationResponse.Status.Completed,
  );
  const canComplete = computed(() => summary.value.canComplete);

  const mandatoryTotal = computed(() => summary.value.mandatoryFormIds.length);
  const mandatoryAnswered = computed(() => mandatoryTotal.value - summary.value.missingMandatoryFormIds.length);
  const totalVisible = computed(() => summary.value.visibleFormIds.length);
  const totalAnswered = computed(() => summary.value.answeredFormIds.length);
  const optionalSkipped = computed(() => summary.value.skippedFormIds.length);

  const mandatoryPct = computed(() =>
    mandatoryTotal.value > 0 ? (mandatoryAnswered.value / mandatoryTotal.value) * 100 : 100,
  );
  const totalPct = computed(() => (totalVisible.value > 0 ? (totalAnswered.value / totalVisible.value) * 100 : 0));

  const sectionProgress = computed<GuidedConsultationSectionProgress[]>(() => {
    const sectionMap = new Map<number, { visibleCount: number; answeredCount: number }>();
    const answeredSet = new Set(summary.value.answeredFormIds);

    for (const formId of summary.value.visibleFormIds) {
      const sectionOrderNum = summary.value.formMeta?.[formId]?.sectionOrderNum ?? 1;
      const section = sectionMap.get(sectionOrderNum) ?? { visibleCount: 0, answeredCount: 0 };

      section.visibleCount += 1;
      if (answeredSet.has(formId)) {
        section.answeredCount += 1;
      }

      sectionMap.set(sectionOrderNum, section);
    }

    return [...sectionMap.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([sectionOrderNum, section]) => ({
        sectionOrderNum,
        visibleCount: section.visibleCount,
        answeredCount: section.answeredCount,
        pct: section.visibleCount > 0 ? (section.answeredCount / section.visibleCount) * 100 : 0,
      }));
  });

  function getFormTitle(formId: number): string {
    const meta = summary.value.formMeta?.[formId];
    if (!meta) return '';
    return getTranslation({ title: meta.title }, 'title', languageEnabled.value) ?? '';
  }

  const missingForms = computed<GuidedConsultationForm[]>(() =>
    summary.value.missingMandatoryFormIds.map((id) => ({
      id,
      title: getFormTitle(id),
      sectionOrderNum: summary.value.formMeta?.[id]?.sectionOrderNum ?? 1,
    })),
  );

  const firstMissingForm = computed<GuidedConsultationForm | null>(() => missingForms.value[0] ?? null);

  function t(key: string, options?: object): string {
    return getLabel(key, languageEnabled.value, options);
  }

  const labels = computed(() => {
    const missing = missingForms.value.length;
    const primaryText = isCompleted.value
      ? t('guided-consultation-progress.submitted')
      : canComplete.value
        ? t('guided-consultation-progress.ready-to-submit')
        : t('guided-consultation-progress.obligatoires');

    let secondaryText = '';
    if (!isCompleted.value) {
      if (canComplete.value && optionalSkipped.value > 0) {
        secondaryText = t('guided-consultation-progress.optional-remaining', {
          plural: optionalSkipped.value,
          named: { count: optionalSkipped.value },
        });
      } else if (!canComplete.value) {
        const mandatoryPart = t('guided-consultation-progress.mandatory-count', {
          named: { answered: mandatoryAnswered.value, total: mandatoryTotal.value },
        });
        const totalPart = t('guided-consultation-progress.total-count', {
          named: { answered: totalAnswered.value, total: totalVisible.value },
        });
        secondaryText = `${mandatoryPart} · ${totalPart}`;
      }
    }

    return {
      primaryText,
      secondaryText,
      actionText: isCompleted.value
        ? ''
        : canComplete.value
          ? t('guided-consultation-progress.submit')
          : t('guided-consultation-progress.continue'),
      completeText: t('guided-consultation-progress.complete'),
      expandText: canComplete.value
        ? t('guided-consultation-progress.see-detail')
        : t('guided-consultation-progress.see-list'),
      collapseText: t('guided-consultation-progress.collapse'),
      panelTitle: canComplete.value
        ? t('guided-consultation-progress.ready-to-submit')
        : t('guided-consultation-progress.your-participation'),
      toCompleteBadge: t('guided-consultation-progress.to-complete-badge'),
      obligatoires: t('guided-consultation-progress.obligatoires'),
      auTotal: t('guided-consultation-progress.au-total'),
      goToFirstMissing: t('guided-consultation-progress.go-to-first-missing'),
      tapToNavigate: t('guided-consultation-progress.tap-to-navigate'),
      remainingMandatory:
        missing > 0
          ? t('guided-consultation-progress.remaining-mandatory', { plural: missing, named: { count: missing } })
          : '',
      mandatoryCountShort: t('guided-consultation-progress.mandatory-count-short', {
        named: { answered: mandatoryAnswered.value, total: mandatoryTotal.value },
      }),
      submittedText: t('guided-consultation-progress.submitted'),
      submitConfirmationTitle: t('guided-consultation-progress.submit-confirmation-title'),
      submitConfirmationBodyStart: t('guided-consultation-progress.submit-confirmation-body-start'),
      submitConfirmationBodyCount: t('guided-consultation-progress.submit-confirmation-body-count', {
        plural: optionalSkipped.value,
        named: { count: optionalSkipped.value },
      }),
      submitConfirmationBodyEnd: t('guided-consultation-progress.submit-confirmation-body-end'),
      submitConfirmationCancel: t('guided-consultation-progress.submit-confirmation-cancel'),
      submitConfirmationConfirm: t('guided-consultation-progress.submit-confirmation-confirm'),
    };
  });

  return {
    summary,
    isCompleted,
    canComplete,
    mandatoryTotal,
    mandatoryAnswered,
    totalVisible,
    totalAnswered,
    mandatoryPct,
    totalPct,
    sectionProgress,
    missingForms,
    firstMissingForm,
    labels,
  };
}
